import { z } from 'zod';

import { AiCurationTier } from '../brand/settings';
import { makeEndpoint } from '../common';

export enum AiCreditFeature {
  CURATION = 'curation',
  EXTRACTION = 'extraction',
}

export enum AiGeneratedExperienceStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AiGeneratedExperienceFeedback {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
}

export enum AiCurationType {
  SIGNATURE = 'Signature',
  EVERYDAY = 'Everyday',
  HOLIDAY = 'Holiday',
}

export enum AiCurationSeason {
  SUMMER = 'Summer',
  WINTER = 'Winter',
  FALL = 'Fall',
  SPRING = 'Spring',
}

export enum AiGenerationSource {
  USER_GENERATED = 'user-generated',
  API_HOLIDAY_CURATION = 'api-holiday-curation',
}

export enum AiCurationHoliday {
  CHRISTMAS = 'Christmas',
  NEW_YEARS = "New Year's",
  THANKSGIVING = 'Thanksgiving',
  VALENTINES_DAY = "Valentine's Day",
  EASTER = 'Easter',
  FOURTH_OF_JULY = '4th of July',
  MOTHERS_DAY = "Mother's Day",
  FATHERS_DAY = "Father's Day",
  HALLOWEEN = 'Halloween',
  LABOR_DAY = 'Labor Day',
  MEMORIAL_DAY = 'Memorial Day',
}

export enum AiCurationAudience {
  COUPLES = 'Couples',
  FAMILIES = 'Families',
  LOCALS = 'Locals',
  VIPS = 'VIPs',
  DAY_GUESTS = 'Day guests',
  SOLO_TRAVELERS = 'Solo Travelers',
  BUSINESS_TRAVELERS = 'Business Travelers',
  FRIEND_GROUPS = 'Friend Groups',
}

export enum AiCurationSpace {
  ROOFTOP = 'Rooftop',
  GARDEN = 'Garden',
  KITCHEN = 'Kitchen',
  BALLROOM = 'Ballroom',
  FARM = 'Farm',
  BAR = 'Bar',
  SPA = 'Spa',
  THEATER = 'Theater',
  WINE_CELLAR = 'Wine Cellar',
  POOL = 'Pool',
  PATIO = 'Patio',
  BEACH = 'Beach',
  TERRACE = 'Terrace',
  LAWN = 'Lawn',
  KIDS_CLUB = "Kid's club",
  LOUNGE = 'Lounge',
  GALLERY = 'Gallery',
  RETAIL = 'Retail',
  LIBRARY = 'Library',
  FITNESS_STUDIO = 'Fitness Studio',
  GYM = 'Gym',
  PRIVATE_DINING_ROOM = 'Private Dining Room (PDR)',
  BARN = 'Barn',
  VINEYARD = 'Vineyard',
  MEDITATION_ROOM = 'Meditation room',
}

export enum AiCurationResource {
  FIREPIT = 'Firepit',
  YOGA_MATS = 'Yoga Mats',
  TENNIS_COURTS = 'Tennis Courts',
  GOLF_COURSE = 'Golf Course',
  GRILLS = 'Grills',
  HORSES = 'Horses',
  CABANAS = 'Cabanas',
  HOUSE_CAR = 'House car',
  SOMMELIER = 'Sommelier',
  MIXOLOGIST = 'Mixologist',
  BARISTA = 'Barista',
  FITNESS_INSTRUCTOR = 'Fitness instructor',
  WELLNESS_INSTRUCTOR = 'Wellness instructor',
  CHEF = 'Chef',
  FARMER = 'Farmer',
  MUSICIANS = 'Musicians',
  DJ = 'DJ',
  ARTIST = 'Artist',
  PHOTOGRAPHER = 'Photographer',
  SPA_THERAPIST = 'Spa therapist',
  BUTLER = 'Butler',
  CAPTAIN = 'Captain',
  KAYAKS = 'Kayaks',
  PADDLEBOARDS = 'Paddleboards',
  SEABOBS = 'Seabobs',
  DAYBEDS = 'Daybeds',
  PICKLEBALL_COURTS = 'Pickleball Courts',
  BIKES = 'Bikes',
  SNORKEL_SETS = 'Snorkel sets',
  FISHING_GEAR = 'Fishing gear',
  BEACH_CHAIRS = 'Beach chairs',
}

// ai exp generation

const CurationBaseSchema = z.object({
  sessionId: z.string().optional(),
});

const SignatureCurationSchema = CurationBaseSchema.extend({
  type: z.literal(AiCurationType.SIGNATURE),
  vision: z.string().nullable(),
  season: z.nativeEnum(AiCurationSeason).nullable(),
  audiences: z.array(z.nativeEnum(AiCurationAudience)).max(2).nullable(),
  spaces: z.array(z.nativeEnum(AiCurationSpace)).max(2).nullable(),
});

const HolidayCurationSchema = CurationBaseSchema.extend({
  type: z.literal(AiCurationType.HOLIDAY),
  vision: z.string().nullable(),
  holiday: z.nativeEnum(AiCurationHoliday).nullable(),
  audiences: z.array(z.nativeEnum(AiCurationAudience)).max(2).nullable(),
  spaces: z.array(z.nativeEnum(AiCurationSpace)).max(2).nullable(),
  holidayId: z.string().uuid().optional(),
});

const EverydayCurationSchema = CurationBaseSchema.extend({
  type: z.literal(AiCurationType.EVERYDAY),
  vision: z.string().nullable(),
  audiences: z.array(z.nativeEnum(AiCurationAudience)).max(2).nullable(),
  resources: z.array(z.nativeEnum(AiCurationResource)).max(3).nullable(),
  spaces: z.array(z.nativeEnum(AiCurationSpace)).max(2).nullable(),
});

const GenerateCuratedExperienceRequestSchema = z.discriminatedUnion('type', [
  SignatureCurationSchema,
  HolidayCurationSchema,
  EverydayCurationSchema,
]);

export type GenerateCuratedExperienceRequest = z.infer<typeof GenerateCuratedExperienceRequestSchema>;
export type SignatureCuration = z.infer<typeof SignatureCurationSchema>;
export type HolidayCuration = z.infer<typeof HolidayCurationSchema>;
export type EverydayCuration = z.infer<typeof EverydayCurationSchema>;

const GenerateAiExperienceAsyncResponseSchema = z.object({
  id: z.string().uuid(),
});

export const GenerateCuratedExperienceEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/ai/curated-experience/generate`,
  responseSchema: GenerateAiExperienceAsyncResponseSchema,
  requestSchema: GenerateCuratedExperienceRequestSchema,
});

// curation options endpoint

const CuratedExperienceOptionsResponseSchema = z.object({
  holidays: z.array(z.nativeEnum(AiCurationHoliday)),
  audiences: z.array(z.nativeEnum(AiCurationAudience)),
  spaces: z.array(z.nativeEnum(AiCurationSpace)),
  resources: z.array(z.nativeEnum(AiCurationResource)),
});

export const GetCuratedExperienceOptionsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `/v2/ai/curated-experience/options`,
  responseSchema: CuratedExperienceOptionsResponseSchema,
  requestSchema: z.void(),
});

const GetBrandAiProfileResponseSchema = z.object({
  id: z.string().uuid().nullable(),
  audiences: z.array(z.nativeEnum(AiCurationAudience)),
  spaces: z.array(z.nativeEnum(AiCurationSpace)),
  resources: z.array(z.nativeEnum(AiCurationResource)),
});

export type GetBrandAiProfileResponse = z.infer<typeof GetBrandAiProfileResponseSchema>;

export const GetBrandAiProfileEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `/v2/ai/brand-ai-profile/${brandId}`,
  responseSchema: GetBrandAiProfileResponseSchema,
  requestSchema: z.void(),
});

// ai profile generation

const GenerateAiProfileRequestSchema = z.object({
  brandIds: z.array(z.string()).optional(),
  includeExistingConfirmedBrandAiProfiles: z.boolean().optional(),
  includeExistingFailedBrandAiProfiles: z.boolean().optional(),
  includeInactiveBrands: z.boolean().optional(),
  includeTestBrands: z.boolean().optional(),
  limit: z.number().int().positive().optional(),
});

const BrandStatsSchema = z.object({
  totalBrands: z.number(),
  testBrands: z.number(),
  deletedBrands: z.number(),
  churnedBrands: z.number(),
  activeBrands: z.number(),
  activeBrandsWithConfirmedProfile: z.number(),
  activeBrandsWithFailedProfile: z.number(),
  activeBrandsWithNoProfile: z.number(),
});

export type BrandStatsForAiProfileGeneration = z.infer<typeof BrandStatsSchema>;

const GenerateAiProfileResponseSchema = z.object({
  message: z.string(),
  brands: z.array(z.object({ id: z.string(), name: z.string() })),
  stats: BrandStatsSchema,
});

export const GenerateAiProfileEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/ai/brand-ai-profile/generate`,
  responseSchema: GenerateAiProfileResponseSchema,
  requestSchema: GenerateAiProfileRequestSchema,
});

// get generated experience by id

const GetAiExperienceResponseSchema = z
  .object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    experienceType: z.nativeEnum(AiCurationType),
    experienceGenerationStatus: z.nativeEnum(AiGeneratedExperienceStatus).nullable(),
    imageGenerationStatus: z.nativeEnum(AiGeneratedExperienceStatus).nullable(),
    experienceGenerationError: z.string().nullable(),
    imageGenerationError: z.string().nullable(),
    experienceAiModel: z.string().nullable(),
    imageAiModel: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .extend({
    imageUrl: z.string().nullable(),
    experienceTitle: z.string().nullable(),
    experienceType: z.nativeEnum(AiCurationType),
    experienceHeadline: z.string().nullable(),
    experienceNarrative: z.string().nullable(),
    holidayInput: z.string().nullable(),
    whyItWorks: z.string().nullable(),
    pricePerGuest: z.number().nullable(),
    guestsPerSession: z.number().nullable(),
    p90BookingWindowDate: z.string().optional(),
    surfaceDate: z.string().optional(),
    feasibility_plan: z
      .object({
        pre_launch_preparation: z.string().nullable(),
        staffing: z.string().nullable(),
        marketing: z.string().nullable(),
        setup: z.string().nullable(),
        execution: z.string().nullable(),
      })
      .nullable(),
  });

export type GetAiExperienceResponse = z.infer<typeof GetAiExperienceResponseSchema>;

export const GetAiExperienceEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ experienceId }: { experienceId: string }) => `/v2/ai/curated-experience/${experienceId}`,
  responseSchema: GetAiExperienceResponseSchema,
  requestSchema: z.void(),
});

// create ai generated experience

const CreateExperienceFromAiRequestSchema = z.object({
  pricePerGuest: z.number().optional(),
  guestsPerSession: z.number().optional(),
});

const CreateExperienceFromAiResponseSchema = z.object({
  experienceId: z.string().uuid(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
});
export type CreateExperienceFromAiRequest = z.infer<typeof CreateExperienceFromAiRequestSchema>;

export type CreateExperienceFromAiResponse = z.infer<typeof CreateExperienceFromAiResponseSchema>;

export const CreateExperienceFromAiEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ aiExperienceId }: { aiExperienceId: string }) => `/v2/ai/curated-experience/${aiExperienceId}/create`,
  responseSchema: CreateExperienceFromAiResponseSchema,
  requestSchema: CreateExperienceFromAiRequestSchema,
});

const AiAutomatedCurationRequestSchema = z.object({
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});

export const AiAutomatedCurationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ generationSource }: { generationSource: AiGenerationSource }) =>
    `/v2/ai/curated-experience/jobs/source/${generationSource}`,
  responseSchema: z.void(),
  requestSchema: AiAutomatedCurationRequestSchema,
});

export const AiAutomatedCurationForBrandEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ generationSource, brandId }: { generationSource: AiGenerationSource; brandId: string }) =>
    `/v2/ai/curated-experience/source/${generationSource}/${brandId}`,
  responseSchema: z.void(),
  requestSchema: AiAutomatedCurationRequestSchema,
});

// submit user feedback about ai generated experience

const SubmitAiExperienceFeedbackRequestSchema = z.object({
  userFeedback: z.nativeEnum(AiGeneratedExperienceFeedback),
});

export const SubmitAiExperienceFeedbackEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ aiExperienceId }: { aiExperienceId: string }) => `/v2/ai/curated-experience/${aiExperienceId}/feedback`,
  responseSchema: z.void(),
  requestSchema: SubmitAiExperienceFeedbackRequestSchema,
});

const AiCurationUsageResponseSchema = z.object({
  used: z.number(),
  limit: z.number().nullable(),
  remaining: z.number().nullable(),
  tier: z.nativeEnum(AiCurationTier),
  isLimitReached: z.boolean(),
});

export type AiCurationUsageResponse = z.infer<typeof AiCurationUsageResponseSchema>;

const DebitAiCreditRequestSchema = z.object({
  feature: z.nativeEnum(AiCreditFeature),
});

export type DebitAiCreditRequest = z.infer<typeof DebitAiCreditRequestSchema>;

export const GetAiCreditsUsageEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `/v2/ai/credits/usage`,
  responseSchema: AiCurationUsageResponseSchema,
  requestSchema: z.void(),
});

export const DebitAiCreditEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/ai/credits/usage`,
  responseSchema: AiCurationUsageResponseSchema,
  requestSchema: DebitAiCreditRequestSchema,
});

export const GetHolidaySuggestionsResponseSchema = z.object({
  items: z.array(GetAiExperienceResponseSchema),
});

export type GetHolidaySuggestionsResponse = z.infer<typeof GetHolidaySuggestionsResponseSchema>;

export const GetHolidaySuggestionsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `/v2/ai/curated-experience/source/holiday-suggestions`,
  responseSchema: GetHolidaySuggestionsResponseSchema,
  requestSchema: z.void(),
});
